import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./api";
import { authFetchJson } from "./auth-fetch";
import { useAuthStore } from "./auth";
import type { components } from "@/api/booqly-api";

/** Calling-user profile. Powers the Personal step in onboarding + future
 *  Settings → Account page. */

export type UserMe = components["schemas"]["UserMeDto"];
export type UserUpdate = components["schemas"]["UpdateUserRequest"];

export function useUserMe() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["user", "me"],
    enabled: !!accessToken,
    refetchOnWindowFocus: false,
    queryFn: async () => unwrap(await api.GET("/api/v1/users/me", {})),
  });
}

/**
 * True once the operator's email is verified. While loading (unknown) we return
 * `true` so we never flash a disabled state or block on a transient null — the
 * server-side write guard is the real enforcement; this only drives UI affordances.
 */
export function useEmailVerified(): boolean {
  const me = useUserMe();
  if (me.isLoading || !me.data) return true;
  return !!me.data.emailVerifiedAt;
}

export function useUpdateUserMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserUpdate) =>
      unwrap(await api.PATCH("/api/v1/users/me", { body })),
    onSuccess: (user) => {
      qc.setQueryData<UserMe>(["user", "me"], (old) => user ?? old);
    },
  });
}

/**
 * Multipart upload of the operator's avatar. Server stores the MinIO
 * key on `users.avatar_url` and returns the refreshed `UserMeDto` with
 * a presigned `avatarUrl`. Cache is updated synchronously to avoid the
 * stale-read race the workspace flow used to hit.
 */
export function useUploadUserAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return authFetchJson<UserMe>("/api/v1/users/me/avatar", {
        method: "POST",
        body: fd,
      });
    },
    onSuccess: (user) => {
      qc.setQueryData<UserMe>(["user", "me"], () => user);
    },
  });
}
